import i18n from '../../i18n/index.ts';
import { formatLocalTime, formatShortDate } from '../../i18n/datetime.ts';
import type { AppointmentStatus } from '@shared/constants/index.ts';
import type { AppointmentSeries } from '@shared/types/index.ts';

/**
 * Schedule-shaped formatting. The plain "put a date or a time in front of a
 * person" formatters live in `i18n/datetime.ts`, where every other screen can
 * find them; what stays here is what only a calendar asks for — a recurrence
 * rule in words, a status badge's colors, and the two timezone reads that
 * turn an instant into the company's own day.
 */

export function weekdayLabel(dayOfWeek: number | null): string {
  if (dayOfWeek === null) return '';
  return i18n.t(`schedule.dayOfWeek.${dayOfWeek}`);
}

function listOf(labels: string[]): string {
  const locale = i18n.language?.startsWith('es') ? 'es' : 'en';
  return new Intl.ListFormat(locale, { type: 'conjunction', style: 'long' }).format(labels);
}

function ruleDays(series: AppointmentSeries): number[] {
  const fromSlots = series.daySlots?.map((slot) => slot.dayOfWeek) ?? [];
  const days = fromSlots.length > 0 ? fromSlots : series.dayOfWeek != null ? [series.dayOfWeek] : [];
  return [...new Set(days)].sort((left, right) => left - right);
}

function ruleNths(series: AppointmentSeries): number[] {
  const fromSlots = (series.daySlots ?? []).flatMap((slot) => slot.nthWeeks ?? []);
  const nths = fromSlots.length > 0 ? fromSlots : series.nthWeek != null ? [series.nthWeek] : [];
  return [...new Set(nths)].sort((left, right) => left - right);
}

function timesVary(series: AppointmentSeries): boolean {
  return (series.daySlots ?? []).some((slot) => slot.startTimeLocal && slot.startTimeLocal !== series.startTimeLocal);
}

/** The rule in words: "Every two weeks on Tuesday at 9:00 AM". */
export function describeRule(series: AppointmentSeries): string {
  const time = formatLocalTime(series.startTimeLocal);
  const vary = timesVary(series);
  switch (series.frequency) {
    case 'one_time':
      return i18n.t('schedule.rule.oneTime', { date: formatShortDate(series.startsOn), time });
    case 'weekly':
    case 'biweekly': {
      const days = ruleDays(series);
      const day = weekdayLabel(days[0] ?? series.dayOfWeek) || formatShortDate(series.startsOn);
      const interval = series.intervalWeeks ?? (series.frequency === 'biweekly' ? 2 : 1);
      const plainWeekly = days.length <= 1 && interval === 1 && series.frequency === 'weekly';
      const plainBiweekly = days.length <= 1 && interval === 2 && series.frequency === 'biweekly';
      if (plainWeekly || plainBiweekly) {
        const key = series.frequency === 'weekly' ? 'schedule.rule.weeklyOn' : 'schedule.rule.biweeklyOn';
        return i18n.t(key, { day, time });
      }
      const dayList = listOf(days.map((value) => weekdayLabel(value)));
      if (days.length > 1 && interval === 1) {
        return i18n.t(vary ? 'schedule.rule.weeklyOnDaysVary' : 'schedule.rule.weeklyOnDays', {
          days: dayList,
          time,
        });
      }
      if (days.length <= 1) {
        return i18n.t(vary ? 'schedule.rule.everyNWeeksOnVary' : 'schedule.rule.everyNWeeksOn', {
          count: interval,
          day,
          time,
        });
      }
      return i18n.t(vary ? 'schedule.rule.everyNWeeksOnDaysVary' : 'schedule.rule.everyNWeeksOnDays', {
        count: interval,
        days: dayList,
        time,
      });
    }
    case 'monthly': {
      const nths = ruleNths(series);
      if (nths.length === 0) {
        return i18n.t('schedule.rule.monthlyDayOfMonth', { day: series.dayOfMonth, time });
      }
      const days = ruleDays(series);
      const nthList = listOf(nths.map((nth) => i18n.t(`schedule.nthWeek.${nth}`).toLowerCase()));
      if (nths.length === 1 && days.length <= 1) {
        return i18n.t('schedule.rule.monthlyNthWeekday', {
          nth: nthList,
          weekday: weekdayLabel(days[0] ?? series.dayOfWeek),
          time,
        });
      }
      const dayList = listOf(days.map((value) => weekdayLabel(value)));
      if (days.length <= 1) {
        return i18n.t(vary ? 'schedule.rule.monthlyNthWeeksVary' : 'schedule.rule.monthlyNthWeeks', {
          nths: nthList,
          weekday: weekdayLabel(days[0] ?? series.dayOfWeek),
          time,
        });
      }
      return i18n.t(vary ? 'schedule.rule.monthlyNthWeekdaysVary' : 'schedule.rule.monthlyNthWeekdays', {
        nths: nthList,
        days: dayList,
        time,
      });
    }
  }
}

/** Badge classes per status, mirroring the quote badge tone system. */
export const APPOINTMENT_TONES: Record<AppointmentStatus, string> = {
  scheduled: 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
  confirmed:
    'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]',
  change_requested: 'border-[color:var(--color-warning)] text-[color:var(--color-warning)]',
  completed: 'border-[color:var(--color-brand)] text-[color:var(--color-brand)]',
  cancelled: 'border-[color:var(--color-line)] text-[color:var(--color-muted)] line-through',
  no_show: 'border-[color:var(--color-danger)] text-[color:var(--color-danger)]',
};

/**
 * Today's calendar date in the *company's* timezone — schedule dates are
 * company-local, so the highlighted "today", list ranges and new-schedule
 * defaults must not drift a day for staff whose device sits in another zone.
 * Falls back to the browser zone when the company is still loading.
 *
 * Always formatted as 'en-CA' regardless of the UI language: this produces a
 * machine-readable YYYY-MM-DD sort key, not a string ever shown to a user.
 */
export function todayInTimezone(timezone?: string | null): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      ...(timezone ? { timeZone: timezone } : {}),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  }
}

/**
 * An instant as the calendar date and wall-clock time somebody in `timezone`
 * would read off it.
 *
 * External calendar events arrive as instants, and the schedule is drawn in
 * company-local days. Without this an event at 8pm on the 11th lands on the
 * 12th for anybody whose browser is east of the company, which is precisely the
 * bug the rest of this file exists to avoid.
 */
export function zonedDateTime(instant: string, timezone?: string | null): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    ...(timezone ? { timeZone: timezone } : {}),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(instant));

  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '00';
  return {
    date: `${value('year')}-${value('month')}-${value('day')}`,
    time: `${value('hour')}:${value('minute')}`,
  };
}

/** 90 → '1h 30m'; 60 → '1h'; 45 → '45m'. */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}m`;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}
